import React, { PropsWithChildren, useMemo } from "react";
import { Body2, styled } from "@iventis/styles";
import { Feature } from "geojson";
import { HoverTooltip } from "@iventis/components";
import {
    getMapObjectAreaSystemDataField,
    getMapObjectIconRotationSystemDataField,
    getMapObjectLengthSystemDataField,
    getMapObjectNameSystemDatafield,
    getMapObjectRotationZOfModelsInAreaSystemDataField,
} from "@iventis/datafields";
import { AnySupportedGeometry, MapObjectProperties } from "@iventis/map-types";
import { useSelector } from "react-redux";
import { IventisErrorBoundary } from "@iventis/error-boundaries";
import { Content } from "@iventis/translations";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { RootState } from "./state/root.store";
import { MapObjectToolTipValues } from "./map-object-map-tooltip-values";
import { getDataFieldsToDisplayOnMapObjectTooltip, getMapObjectPropertiesToDisplayOnMapObjectTooltip } from "./map-object-map-tooltip-helpers";
import { MapObjectMapTooltipSplitLineComponent } from "./map-object-map-tooltip-split-line";

export type MapObjectTooltipProps = {
    layerId: string;
    mapObject: Feature<AnySupportedGeometry, MapObjectProperties>;
    cursorX: number;
    cursorY: number;
    containerElementLeft: number;
    containerElementRight: number;
    containerElementTop: number;
};

export function MapObjectMapTooltipComponent({
    cursorX,
    cursorY,
    containerElementLeft,
    containerElementRight,
    containerElementTop,
    layerId,
    mapObject,
}: PropsWithChildren<MapObjectTooltipProps>) {
    const translate = useIventisTranslate();
    return (
        <HoverTooltip
            cursorX={cursorX}
            cursorY={cursorY}
            containerElementLeft={containerElementLeft}
            containerElementRight={containerElementRight}
            containerElementTop={containerElementTop}
            testId="map-object-tooltip"
        >
            <IventisErrorBoundary size="custom" customComponent={<Body2>{translate(Content.map9.dataFieldsInTooltip.error)}</Body2>}>
                <MapObjectTooltipContent layerId={layerId} mapObject={mapObject} />
            </IventisErrorBoundary>
        </HoverTooltip>
    );
}

function MapObjectTooltipContent({ layerId, mapObject }: Pick<MapObjectTooltipProps, "layerId" | "mapObject">) {
    const {
        layers,
        mapObjectNameDataFieldId,
        mapObjectAreaDataFieldId,
        mapObjectLengthDataFieldId,
        mapObjectRotationZOfModelsInAreaDataFieldId,
        mapObjectIconRotationDataFieldId,
        unitOfMeasurement,
        geoJSON,
    } = useSelector(
        (state: RootState) => ({
            layers: state.mapReducer.mapModule.layers,
            mapObjectNameDataFieldId: getMapObjectNameSystemDatafield(state.mapReducer.mapModule.projectDataFields)?.id,
            mapObjectAreaDataFieldId: getMapObjectAreaSystemDataField(state.mapReducer.mapModule.projectDataFields)?.id,
            mapObjectLengthDataFieldId: getMapObjectLengthSystemDataField(state.mapReducer.mapModule.projectDataFields)?.id,
            mapObjectRotationZOfModelsInAreaDataFieldId: getMapObjectRotationZOfModelsInAreaSystemDataField(state.mapReducer.mapModule.projectDataFields)?.id,
            mapObjectIconRotationDataFieldId: getMapObjectIconRotationSystemDataField(state.mapReducer.mapModule.projectDataFields)?.id,
            unitOfMeasurement: state.mapReducer.mapModule.unitOfMeasurement,
            geoJSON: state.mapReducer.mapModule.geoJSON,
        }),
        (currentState, previousState) =>
            currentState.layers.stamp === previousState.layers.stamp &&
            currentState.unitOfMeasurement.stamp === previousState.unitOfMeasurement.stamp &&
            currentState.geoJSON.stamp === previousState.geoJSON.stamp
    );

    const layer = useMemo(() => layers.value.find((l) => l.id === layerId), [layers.stamp, layerId]);
    const { layerName, dataFieldsToDisplay } = useMemo(() => getDataFieldsToDisplayOnMapObjectTooltip(layer, mapObjectNameDataFieldId), [
        layerId,
        layers?.stamp,
        mapObjectNameDataFieldId,
    ]);

    const mapObjectProperties = useMemo(() => getMapObjectPropertiesToDisplayOnMapObjectTooltip(mapObject, geoJSON.value), [mapObject.id, geoJSON.stamp]);

    const isMapObjectNameDataFieldDisplayed = dataFieldsToDisplay?.some((dataField) => dataField.id === mapObjectNameDataFieldId);

    if (mapObject.properties.splitLine) {
        return <MapObjectMapTooltipSplitLineComponent splitLine={mapObject.properties.splitLine} unitOfMeasurement={unitOfMeasurement.value} />;
    }

    return (
        <>
            <LayerName data-testid="map-object-tooltip-value-layer" $addMargin={!isMapObjectNameDataFieldDisplayed}>
                <span>{layerName}</span> {layer.locked && <FontAwesomeIcon icon={["fas", "lock"]} data-testid="map-object-tooltip-lock-icon" />}
            </LayerName>
            {dataFieldsToDisplay.map((dataField) => (
                <MapObjectToolTipValues
                    key={dataField.id}
                    id={dataField.id}
                    name={dataField.name}
                    type={dataField.type}
                    mapObjectDataFieldValues={mapObjectProperties}
                    mapObjectNameDataFieldId={mapObjectNameDataFieldId}
                    mapObjectAreaDataFieldId={mapObjectAreaDataFieldId}
                    mapObjectLengthDataFieldId={mapObjectLengthDataFieldId}
                    mapObjectRotationZOfModelsInAreaDataFieldId={mapObjectRotationZOfModelsInAreaDataFieldId}
                    mapObjectIconRotationDataFieldId={mapObjectIconRotationDataFieldId}
                    unitOfMeasurement={unitOfMeasurement.value}
                    amountOfValues={dataFieldsToDisplay.length}
                    layer={layer}
                />
            ))}
        </>
    );
}

const LayerName = styled(Body2)<{ $addMargin: boolean }>`
    font-weight: 500;
    margin-bottom: ${({ $addMargin }) => ($addMargin ? "10px" : "0px")};

    svg {
        margin-left: 5px;
    }
`;
